import {
  Avatar,
  Button,
  Chip,
  Collapse,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Paper,
  Tooltip,
  Typography
} from '@material-ui/core';
import { Theme, createStyles, makeStyles } from '@material-ui/core/styles';
import {
  ExpandLessRounded,
  ExpandMoreRounded,
  FiberNewRounded,
  InfoOutlined,
  UpdateRounded
} from '@material-ui/icons';
import clsx from 'clsx';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { UpdateProject } from '../api/projects';
import { RootState } from '../app/store';
import backspaceLogo from '../assets/backspacelogo64.png';
import { useAppDispatch } from '../hooks/useAppRudux';
import useDeviceScreen from '../hooks/useDeviceScreen';
import loadingSlice from '../pages/Loading/loadingSlice';
import {
  ProjectRegister,
  getProjectRegisterInfoAsync
} from '../pages/Project/projectRegisterSlice';
import { Project, updateProjectAsync } from '../pages/Project/projectSlice';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    card: {
      marginBottom: theme.spacing(1.5),
      padding: theme.spacing(1.5),
      borderRadius: theme.spacing(2)
    },
    title: {
      display: 'flex',
      alignItems: 'center'
    },
    headerText: {
      flexGrow: 1,
      lineHeight: '125%'
    },
    divider: {
      margin: theme.spacing(0.5, 0, 1.5, 0),
      width: theme.spacing(4)
    },
    dialogContent: {
      padding: theme.spacing(0, 3, 2, 3)
    },
    flexAlignCenter: {
      display: 'flex',
      alignItems: 'center'
    },
    alignItemCenterRight: {
      display: 'flex',
      justifyContent: 'flex-end',
      alignItems: 'center'
    },
    gutterBottom: {
      marginBottom: '5.6px'
    },
    marginTop: {
      marginTop: theme.spacing(1)
    },
    connectButton: {
      textTransform: 'none'
    },
    connectButtonIcon: {
      width: 16,
      height: 16
    },
    flexAction: {
      display: 'flex',
      '& > *': {
        margin: theme.spacing(1)
      },
      [theme.breakpoints.down('xs')]: {
        flexDirection: 'column-reverse',
        width: `calc(100% - ${theme.spacing(2)}px)`,
        '& > *': {
          marginTop: 0
        }
      }
    },
    newIcon: { margin: theme.spacing(0, 0.5, 0, -0.5) },
    isMobile: {
      [theme.breakpoints.up('md')]: {
        display: 'none'
      }
    }
  })
);

export default function ProjectInfoCard({
  project,
  showSpaceConnect = false
}: {
  project: Omit<Project, 'users'>;
  showSpaceConnect?: boolean;
}) {
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const { IS_TABLET } = useDeviceScreen();
  const { t } = useTranslation();

  const { project: projectInfo } = useSelector(
    (state: RootState) => state.projectRegisters
  );

  const [openDetail, setOpenDetail] = useState<boolean>(false);
  const [openConnect, setOpenConnect] = useState<boolean>(false);

  function renderInfo(topic: string, oldValue: any, newValue: any) {
    const isUpdated = oldValue !== newValue;
    return (
      <div className={classes.flexAlignCenter}>
        {isUpdated && (
          <FiberNewRounded className={classes.newIcon} color="primary" />
        )}
        <Typography color={isUpdated ? 'primary' : 'textPrimary'}>
          <b>{topic}:</b> {newValue || '-'}
        </Typography>
      </div>
    );
  }

  function handleClickConnect(open: boolean) {
    return function () {
      try {
        if (open) {
          dispatch(
            getProjectRegisterInfoAsync(project.projectRegisterId)
          ).unwrap();
          dispatch(loadingSlice.actions.success({ message: t('projectInfoCard.messages.connected') }));
        }
        setOpenConnect(open);
      } catch {}
    };
  }

  function handleClickUpdate(projectRegister: ProjectRegister) {
    const updateProject: UpdateProject = {
      id: project.id,
      projectRegisterId: projectRegister.id,
      projectNo: projectRegister.projectNo,
      name: projectRegister.name,
      plantName: projectRegister.plantName,
      customerCompanyName: projectRegister.customerCompanyName,
      productName: projectRegister.productName,
      businessUnitName: projectRegister.businessUnitName,
      businessUnitGroupName: projectRegister.businessUnitGroupName,
      projectStatus: projectRegister.projectStatus
    };
    try {
      dispatch(updateProjectAsync(updateProject)).unwrap();
      dispatch(
        loadingSlice.actions.success({ message: t('projectInfoCard.messages.projectUpdated') })
      );
      setOpenConnect(false);
    } catch {}
  }

  return (
    <div>
      <Dialog fullWidth open={openConnect} onClose={handleClickConnect(false)}>
        <DialogTitle>{t('projectInfoCard.dialogTitle')}</DialogTitle>
        {projectInfo && (
          <DialogContent className={classes.dialogContent}>
            {renderInfo(
              t('projectInfoCard.projectNo'),
              project.projectNo,
              projectInfo.projectNo
            )}
            {renderInfo(t('projectInfoCard.projectName'), project.name, projectInfo.name)}
            {renderInfo(
              t('projectInfoCard.customerCompany'),
              project.customerCompanyName,
              projectInfo.customerCompanyName
            )}
            {renderInfo(t('projectInfoCard.plant'), project.plantName, projectInfo.plantName)}
            {renderInfo(
              t('projectInfoCard.product'),
              project.productName,
              projectInfo.productName
            )}
            {renderInfo(
              t('projectInfoCard.businessUnit'),
              project.businessUnitName,
              projectInfo.businessUnitName
            )}
            {renderInfo(
              t('projectInfoCard.businessUnitGroup'),
              project.businessUnitGroupName,
              projectInfo.businessUnitGroupName
            )}
            {renderInfo(
              t('projectInfoCard.status'),
              project.projectStatus,
              projectInfo.projectStatus
            )}
          </DialogContent>
        )}
        <div className={classes.flexAction}>
          <Button
            color="primary"
            variant="contained"
            size="large"
            fullWidth
            startIcon={<UpdateRounded />}
            disabled={!Boolean(projectInfo)}
            onClick={() => projectInfo && handleClickUpdate(projectInfo)}
          >
            {t('projectInfoCard.update')}
          </Button>
          <Button fullWidth onClick={handleClickConnect(false)}>
            {t('projectInfoCard.cancel')}
          </Button>
        </div>
      </Dialog>
      <Paper className={classes.card}>
        <div className={classes.flexAlignCenter}>
          <Typography
            className={classes.headerText}
            variant="subtitle1"
            gutterBottom
          >
            {t('projectInfoCard.projectInformation')}
          </Typography>
          <IconButton
            className={classes.isMobile}
            size="small"
            onClick={() => setOpenDetail(!openDetail)}
          >
            {openDetail ? <ExpandLessRounded /> : <ExpandMoreRounded />}
          </IconButton>
        </div>
        <Collapse in={IS_TABLET && !openDetail}>
          <Typography variant="subtitle2">{project.name}</Typography>
        </Collapse>
        <Collapse in={!IS_TABLET || openDetail}>
          <Divider className={classes.divider} />
          <Typography variant="subtitle2">{t('projectInfoCard.projectNo')}</Typography>
          <Typography variant="body1" gutterBottom>
            {project?.projectNo}
          </Typography>
          <Typography variant="subtitle2">{t('projectInfoCard.projectName')}</Typography>
          <Typography variant="body1" gutterBottom>
            {project.name}
          </Typography>
          <Typography variant="subtitle2">{t('projectInfoCard.customerCompany')}</Typography>
          <Typography variant="body1" gutterBottom>
            {project.customerCompanyName}
          </Typography>
          <Typography variant="subtitle2">{t('projectInfoCard.plant')}</Typography>
          <Typography variant="body1" gutterBottom>
            {project.plantName}
          </Typography>
          <Typography variant="subtitle2">{t('projectInfoCard.product')}</Typography>
          <div className={clsx(classes.flexAlignCenter, classes.gutterBottom)}>
            <Typography variant="body1">
              {project.productName || <i>{t('projectInfoCard.informationNotProvided')}</i>}
            </Typography>
            <Tooltip
              title={`${project.productName || t('projectInfoCard.productNameNotProvided')}, ${
                project.businessUnitName || t('projectInfoCard.businessUnitNameNotProvided')
              }, ${
                project.businessUnitGroupName ||
                t('projectInfoCard.businessUnitGroupNameNotProvided')
              }`}
            >
              <IconButton size="small">
                <InfoOutlined fontSize="small" />
              </IconButton>
            </Tooltip>
          </div>
          <div className={classes.alignItemCenterRight}>
            <Tooltip title={t('projectInfoCard.projectStatusTooltip') as string} arrow>
              <Chip
                variant="outlined"
                size="small"
                label={project.projectStatus}
              />
            </Tooltip>
          </div>
          {showSpaceConnect && (
            <>
              <Typography
                className={classes.marginTop}
                component="div"
                variant="caption"
              >
                {t('projectInfoCard.updateInstruction')}
              </Typography>
              <Button
                className={classes.connectButton}
                color="primary"
                variant="outlined"
                size="small"
                fullWidth
                startIcon={
                  <Avatar
                    className={classes.connectButtonIcon}
                    src={backspaceLogo}
                  />
                }
                onClick={handleClickConnect(true)}
              >
                {t('projectInfoCard.spaceProjectConnect')}
              </Button>
            </>
          )}
        </Collapse>
      </Paper>
    </div>
  );
}
